import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';

export interface StepDescriptor<K extends string = string> {
  key: K;
  label: ReactNode;
}

/**
 * Disc, the glyph inside it, and the label below — one entry, because they only read as one
 * control while they move together. The check was `h-3.5 w-3.5` against a 24px disc and would
 * have filled a 20px one edge to edge.
 */
const SIZES = {
  sm: { head: 'h-[18px] w-[18px] text-[10px]', glyph: 'h-2.5 w-2.5', label: 'text-[11px]' },
  md: { head: 'h-5 w-5 text-[11px]', glyph: 'h-3 w-3', label: 'text-xs' },
} as const;

/**
 * Numbered step rail — reference §14.
 *
 * Purely presentational: it renders heads and a connector and nothing else. The parent owns
 * `activeIndex`, the panel content, and every validity rule. This component never mutates
 * `activeIndex`, so step logic lives in one place rather than being split between the rail
 * and its host.
 *
 * **The filled head is `brand-500`** — the product's actual brand yellow, the Create button's
 * ground. Product call, 11 Sep 2026: it shipped at `brand-700` for contrast and was rejected on
 * sight as too dark to read as the brand colour.
 *
 * ```
 * brand-500 on white   2.16:1   BELOW WCAG 1.4.11's 3:1 — accepted knowingly
 * brand-700 on white   3.42:1   clears it, and reads brown rather than yellow
 * brand-800 on white   5.76:1   clears AA for the step number too
 * ```
 *
 * What makes the sub-3:1 defensible is that **the disc's colour is not the only signal**, which
 * is the same argument the sidebar's brand-500 active bar runs on. A completed head carries a
 * `<Check>` where a pending one carries its number, and the active step's label is
 * `text-slate-900 font-medium` against `slate-400` for every other. Strip the fill out entirely
 * and the rail still says which steps are done and which one you are on — so do not remove
 * either of those two signals to simplify something, because the colour is not carrying this.
 *
 * **The rail owns the space around it** — `mt-5` and `mb-4`, on top of what the modal body
 * already gives it. The gap above the discs is 40px against the body’s own 20px of padding, and
 * the gap below the labels is 32px against the 16px between every other pair of fields.
 *
 * That separation is the whole point: the rail is not a form row, and without it the step
 * labels read as headings for the first row of fields under them. It lives in the component
 * rather than at the two call sites, because a margin spelled out twice is a margin that ends
 * up spelled differently.
 *
 * The connector is `brand-300` (1.48:1) rather than `brand-200` (1.26:1), which would sit
 * below the `slate-200` it has to be distinguishable FROM — a completed run that reads as an
 * incomplete one is worse than no colour at all. Sub-3:1 is defensible there because the line
 * is a second signal, not the only one.
 */
export function Stepper<K extends string = string>({
  steps,
  activeIndex,
  completedKeys,
  onJump,
  size = 'md',
  id,
}: {
  steps: StepDescriptor<K>[];
  activeIndex: number;
  /** Overrides the default "index &lt; activeIndex" completion rule. */
  completedKeys?: Set<K>;
  /** Supplying this makes the heads clickable. */
  onJump?: (index: number) => void;
  size?: 'sm' | 'md';
  id?: string;
}) {
  const { head, glyph, label } = SIZES[size];

  return (
    <ol className="mb-4 mt-5 flex items-start" role="tablist" aria-label="Steps">
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const isComplete = completedKeys ? completedKeys.has(step.key) : index < activeIndex;
        const interactive = Boolean(onJump);

        const Head = interactive ? 'button' : 'div';

        return (
          <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center">
            <div className="flex w-full items-center">
              {/* Boundary segments keep the connector flush with the rail's edges. */}
              <span
                aria-hidden="true"
                className={`h-px flex-1 ${index === 0 ? 'bg-transparent' : isComplete || isActive ? 'bg-brand-300' : 'bg-slate-200'}`}
              />

              <Head
                {...(interactive
                  ? { type: 'button' as const, onClick: () => onJump?.(index) }
                  : {})}
                id={id ? `${id}-step-${step.key}` : undefined}
                role="tab"
                aria-selected={isActive}
                className={[
                  'flex shrink-0 items-center justify-center rounded-full font-medium tabular-nums transition',
                  head,
                  isActive
                    ? 'bg-brand-500 text-white ring-2 ring-brand-100'
                    : isComplete
                      ? 'bg-brand-500 text-white'
                      : 'bg-white text-slate-400 ring-1 ring-slate-300',
                  interactive ? 'cursor-pointer' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isComplete && !isActive ? (
                  <Check className={glyph} aria-hidden="true" />
                ) : (
                  index + 1
                )}
              </Head>

              <span
                aria-hidden="true"
                className={`h-px flex-1 ${index === steps.length - 1 ? 'bg-transparent' : isComplete ? 'bg-brand-300' : 'bg-slate-200'}`}
              />
            </div>

            {/* The label truncates, so the full step name belongs in the product’s own tooltip. */}
            <Tooltip
              label={typeof step.label === 'string' ? step.label : undefined}
              block
            >
            <span
              className={[
                // `block` is load-bearing: a <span> is display:inline, where width and
                // text-align are both ignored. It used to be a direct flex item of the <li>,
                // which blockified it — wrapping it in <Tooltip> made it an ordinary inline
                // child again and the labels silently went left-aligned under centred heads.
                'mt-1.5 block w-full truncate px-1 text-center',
                label,
                isActive ? 'font-medium text-slate-900' : 'text-slate-400',
              ].join(' ')}
            >
              {step.label}
            </span>
            </Tooltip>
          </li>
        );
      })}
    </ol>
  );
}
